import { Check, X, CloudRain, ArrowRight } from "lucide-react";
import { Reveal, ChapterTitle } from "@/components/Reveal";
import { LA_FORMULES, LA_PRIX_POURQUOI, LA_COMPARATIF, LA_INCLUS, LA_NON_INCLUS, LA_POUR_QUI, LA_METEO } from "@/data/lieuxAnciens";

export const LieuxAnciensFormules = ({ onPrivee }) => (
  <section id="formules-lieux-anciens" className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 lg:pb-32 scroll-mt-24" data-testid="la-formules">
    <Reveal className="max-w-2xl mb-12">
      <p className="eyebrow mb-6">Formules & tarifs</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">Une journée collective à date fixe, <em className="not-italic text-terra">ou une création privée sur demande.</em></h2>
    </Reveal>
    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
      {LA_FORMULES.map((f, i) => (
        <Reveal key={f.id} delay={i * 0.08} className="h-full">
          <article className={`h-full rounded-2xl p-7 flex flex-col ${i === 0 ? "bg-earth grain text-sand" : "bg-card border border-sand-dark"}`} data-testid={`la-formule-${f.id}`}>
            <div className="relative z-[2] flex-1">
              <span className={`font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full ${i === 0 ? "bg-terra text-earth" : "bg-sand-dark text-bark"}`}>{f.badge}</span>
              <h3 className={`font-serif text-2xl mt-6 leading-tight ${i === 0 ? "" : "text-bark"}`}>{f.titre}</h3>
              <p className="font-serif text-xl mt-1 text-terra" data-testid={`la-prix-${f.id}`}>{f.prix}</p>
              <ul className={`mt-5 space-y-2.5 text-sm ${i === 0 ? "text-sand/75" : "text-fumee"}`}>{f.points.map((p) => <li key={p} className="flex gap-3"><Check className="w-4 h-4 text-terra shrink-0 mt-0.5" />{p}</li>)}</ul>
            </div>
            {f.id === "collectif" ? (
              <a href={f.href} data-testid={`la-cta-${f.id}`} className="relative z-[2] mt-7 inline-flex self-start items-center gap-2 px-6 py-3 rounded-full text-sm bg-terra text-earth hover:bg-sand transition-colors duration-300">{f.cta} <ArrowRight className="w-4 h-4" /></a>
            ) : (
              <button type="button" onClick={() => onPrivee(f.id)} data-testid={`la-cta-${f.id}`} className="relative z-[2] mt-7 inline-flex self-start items-center gap-2 px-6 py-3 rounded-full text-sm bg-earth text-sand hover:bg-terra hover:text-earth transition-colors duration-300">{f.cta} <ArrowRight className="w-4 h-4" /></button>
            )}
          </article>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-10 max-w-3xl">
      <p className="text-sm text-bark/85 leading-relaxed rounded-xl border border-terra/30 bg-terra/5 p-5" data-testid="la-prix-pourquoi"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra block mb-2">Pourquoi ce tarif</span>{LA_PRIX_POURQUOI}</p>
    </Reveal>
  </section>
);

export const LieuxAnciensComparatif = () => (
  <section className="bg-sand-dark/50 border-y border-sand-dark" data-testid="la-comparatif">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
      <Reveal className="max-w-2xl mb-10">
        <p className="eyebrow mb-6">Orbe ou lieu ancien ?</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">Deux cadres pour la même création.</h2>
      </Reveal>
      <Reveal>
        <div className="hidden md:block bg-card border border-sand-dark rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="la-comparatif-table">
            <thead><tr className="border-b border-sand-dark font-mono text-[10px] uppercase tracking-[0.2em] text-fumee text-left"><th className="px-5 py-4 font-normal w-40" /><th className="px-5 py-4 font-normal">À l'atelier d'Orbe</th><th className="px-5 py-4 font-normal text-terra">Sur un lieu ancien</th></tr></thead>
            <tbody className="divide-y divide-sand-dark">{LA_COMPARATIF.map((r) => <tr key={r.critere}><td className="px-5 py-4 font-serif text-lg text-bark">{r.critere}</td><td className="px-5 py-4 text-fumee">{r.orbe}</td><td className="px-5 py-4 text-bark/85">{r.lieu}</td></tr>)}</tbody>
          </table>
        </div>
        <ul className="md:hidden space-y-3" data-testid="la-comparatif-mobile">
          {LA_COMPARATIF.map((r) => (
            <li key={r.critere} className="bg-card border border-sand-dark rounded-2xl p-5">
              <p className="font-serif text-lg text-bark">{r.critere}</p>
              <p className="text-xs text-fumee mt-2"><span className="font-mono text-[10px] uppercase tracking-[0.15em] block">Orbe</span>{r.orbe}</p>
              <p className="text-xs text-bark/85 mt-2"><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-terra block">Lieu ancien</span>{r.lieu}</p>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

export const LieuxAnciensInclus = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12" data-testid="la-inclus">
    <Reveal className="lg:col-span-5">
      <ChapterTitle numero="Chapitre II" titre="Ce qui est compris" />
      <ul className="space-y-3 text-sm text-bark/85">{LA_INCLUS.map((p) => <li key={p} className="flex gap-3"><Check className="w-4 h-4 text-terra shrink-0 mt-0.5" />{p}</li>)}</ul>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee mt-10 mb-4">Non compris</p>
      <ul className="space-y-3 text-sm text-fumee" data-testid="la-non-inclus">{LA_NON_INCLUS.map((p) => <li key={p} className="flex gap-3"><X className="w-4 h-4 text-fumee/60 shrink-0 mt-0.5" />{p}</li>)}</ul>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-7">
      <ChapterTitle numero="Chapitre III" titre="À qui s'adresse cette expérience ?" />
      <div className="grid sm:grid-cols-2 gap-5" data-testid="la-pour-qui">
        {LA_POUR_QUI.map((p, i) => <div key={p.titre} className="bg-card border border-sand-dark rounded-2xl p-6"><span className="font-mono text-xs text-terra tracking-[0.3em]">0{i + 1}</span><h3 className="font-serif text-xl text-bark mt-3 mb-2">{p.titre}</h3><p className="text-sm text-fumee leading-relaxed">{p.texte}</p></div>)}
      </div>
    </Reveal>
  </section>
);

export const LieuxAnciensMeteo = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28" data-testid="la-meteo">
    <Reveal className="bg-card border border-sand-dark rounded-2xl p-8 lg:p-10 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4">
        <CloudRain className="w-6 h-6 text-terra mb-4" />
        <p className="eyebrow mb-3">Conditions météo & report</p>
        <h3 className="font-serif text-2xl text-bark leading-tight">Dehors, on compose avec le ciel.</h3>
      </div>
      <div className="lg:col-span-8 space-y-3">{LA_METEO.map((t) => <p key={t} className="text-sm text-bark/85 leading-relaxed">{t}</p>)}</div>
    </Reveal>
  </section>
);
